import React, { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { createHashRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom';
import { UserProvider, UserContext } from './Components/UserContext';
import BackgroundMedia from './Components/BackgroundMedia';
import NavBar from './NavBar';
import LoadingScreen from './screens/Loading';
import ErrorPage from './screens/ErrorPage';
import 'bootstrap/dist/css/bootstrap.min.css';

const Home = lazy(() => import('./screens/Home'));
const SignIn = lazy(() => import('./screens/SignIn'));
const Map = lazy(() => import('./screens/Map'));
const ShowMarkers = lazy(() => import('./screens/ShowMarkers'));
const HodAkevGroups = lazy(() => import('./screens/HodAkevGroups'));
const MapBoxMaps = lazy(() => import('./screens/MapBoxMaps'));
const MapBoxTerrin = lazy(() => import('./screens/MapBoxTerrin'));
const Fota = lazy(() => import('./screens/Fota'));
const MgConnect = lazy(() => import('./screens/MgConnect'));
const Admin = lazy(() => import('./screens/Admin'));

// Private route wrapper
function PrivateRouteWrapper() {
  const { loggedIn } = React.useContext(UserContext);

  return loggedIn ? (
    <>
      <NavBar />
      <Outlet />
    </>
  ) : (
    <Navigate to="/" replace />
  );
}

// Routes definition
const router = createHashRouter(
  [
    {
      path: '/',
      element: (
        <>
          <BackgroundMedia/>
          <Home />
        </>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: '/signIn',
      element: (
        <>
          <BackgroundMedia />
          <SignIn />
        </>
      ),
      errorElement: <ErrorPage />,
    },
    {
      element: <PrivateRouteWrapper />,
      errorElement: <ErrorPage />,
      children: [
        { path: '/map', element: <Map /> },
        { path: '/3dmap', element: <MapBoxMaps /> },
        { path: '/showMarkers', element: <ShowMarkers /> },
        { path: '/hodAkevGroups', element: <HodAkevGroups /> },
        { path: '/mapBoxTerrin', element: <MapBoxTerrin /> },
        { path: '/fota', element: <Fota /> },
        { path: '/mgConnect', element: <MgConnect/> },
        { path: '/admin', element: <Admin/> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/" replace />,
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true, // Enables relative paths in nested routes
      v7_fetcherPersist: true,   // Retains fetcher state during navigation
      v7_normalizeFormMethod: true, // Normalizes form methods (e.g., POST or GET)
      v7_partialHydration: true, // Supports partial hydration for server-side rendering
      v7_skipActionErrorRevalidation: true, // Prevents revalidation when action errors occur // Opt-in to startTransition wrapping
    },
  }
);

// Render the app
const rootElement = document.getElementById('root');
createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary fallback={<ErrorPage />}>
      <UserProvider>
        <Suspense fallback={<LoadingScreen/>}>
          <RouterProvider future={{ v7_startTransition: true }} router={router} />
        </Suspense>
      </UserProvider>
    </ErrorBoundary>
  </React.StrictMode>
);